import { useEffect, useRef } from 'react';
import { usePlaidLink } from 'react-plaid-link';

export interface PlaidLinkSuccess {
  publicToken: string;
  linkSessionId: string;
  institution: { institution_id: string; name: string } | null;
}

interface PlaidLinkBoundaryProps {
  linkToken: string;
  mode: 'new' | 'update';
  onSuccess: (result: PlaidLinkSuccess) => void;
  onExit: (linkSessionId: string | null) => void;
  onError: (message: string, linkSessionId?: string | null) => void;
}

/**
 * React-owned Plaid SDK boundary. The Link and public tokens remain in memory:
 * callers receive only the short-lived onSuccess values they must send once.
 */
export function PlaidLinkBoundary({ linkToken, mode: _mode, onSuccess, onExit, onError }: PlaidLinkBoundaryProps) {
  const opened = useRef(false);
  const { ready, open, error } = usePlaidLink({
    token: linkToken,
    onSuccess: (publicToken, metadata) => {
      onSuccess({
        publicToken,
        linkSessionId: metadata.link_session_id,
        institution: metadata.institution,
      });
    },
    onExit: (linkError, metadata) => {
      if (linkError) {
        onError('Plaid Link could not complete. Your connection has not been confirmed.', metadata.link_session_id || null);
        return;
      }
      onExit(metadata.link_session_id || null);
    },
  });

  useEffect(() => {
    if (error) {
      onError('Plaid Link could not load. Try again when the service is available.');
      return;
    }
    if (ready && !opened.current) {
      opened.current = true;
      open();
    }
  }, [error, onError, open, ready]);

  return null;
}
